import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoginForm from '../components/Auth/LoginForm';
import SiteFooter from '../components/common/SiteFooter';
import BrandLogo from '../components/common/BrandLogo';
import BrandBackdrop from '../components/common/BrandBackdrop';
import './Login.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { takeReturnTo } from '../utils/returnTo';

/**
 * Login page component
 * @returns {JSX.Element} Login page component
 */
const Login: React.FC = () => {
  useDocumentTitle('Sign in');
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const redirected = useRef(false);

  // Each sign-in method handles its own errors.

  // Redirect if already authenticated: back to the page that asked for sign-in, else the dashboard.
  // The saved page is read once, so StrictMode's repeated effects can't lose it.
  useEffect(() => {
    if (!isLoading && isAuthenticated && !redirected.current) {
      redirected.current = true;
      navigate(takeReturnTo() ?? '/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Don't render if authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="login-page">
      <BrandBackdrop />
      <div className="login-page__container">
        <div className="login-page__branding">
          <h1 className="sr-only">Sign in to iKompete</h1>
          <Link to="/" className="login-page__logo" aria-label="iKompete home">
            <BrandLogo reversed />
          </Link>
          
          <div className="login-page__description">
            <h2 className="login-page__slogan">
              <span className="login-page__word login-page__word--blue">Create.</span>{' '}
              <span className="login-page__word login-page__word--orange">Compete.</span>{' '}
              <span className="login-page__word login-page__word--blue">Advance.</span>
            </h2>
          </div>
        </div>
        
        <div className="login-page__form-container">
          {/* No fixed destination: once signed in, the route guard returns to the saved page or /dashboard. */}
          <LoginForm />
        </div>
      </div>

      <SiteFooter minimal showLogo={false} />
    </div>
  );
};

export default Login;
